<template>
    <div class="container-fluid content-type-33">
        <div class="container">
            <div class="row row-content">
                <div v-if="hasMediumScreen || showPanel" class="col-12 col-md-6 col-lg-5 col-xl-4 left-column">
                    <div class="left-section full-height">
                        <div class="header" v-if="!hasMediumScreen">
                            <CompaniesHeader
                                :total="total"
                                :loading="loading"
                            />
                        </div>

                        <CompaniesTitle v-if="hasMediumScreen" :showMode="companiesMode !== 'prospects' || !!audienceId" />

                        <div class="main" :class="{ 'xxl-border': currentCompanyIndex !== null, 'glass': (showPanel && !hasMediumScreen)}">
                            <div class="options" v-if="currentCompanyIndex === null">
                                <CompaniesQuickSearch v-if="hasMediumScreen && (companiesMode === 'prospects' || (companiesMode === 'all' && !!audienceId))"/>
                                <div v-if="!quickSearch && companiesMode === 'all'">
                                    <!-- <div class="title">{{$t('views.companies.options_main')}}</div> -->
                                    <CompaniesOptions />
                                </div>
                                <div v-if="!quickSearch && companiesMode === 'prospects'">
                                    <!-- <div class="title">{{$t('views.companies.options_prospects')}}</div> -->
                                    <ProspectsOptions />
                                </div>
                                <CompaniesQuickSearch v-if="hasMediumScreen && companiesMode === 'all' && !audienceId"/>
                            </div>

                            <template v-else>
                                <LoadingSection v-if="loading" />

                                <template v-else>
                                    <div v-scroll-shadow="{ directions: ['vertical']}" class="nav" v-if="!hasNoCredits">
                                        <!-- <div class="title" v-if="!loading">{{$t('views.companies.nav_prefs')}}</div> -->
                                        <CompanyDetailsNav />
                                    </div>
                                    <div v-scroll-shadow="{ directions: ['vertical']}" class="list" v-if="companies.length > 1">
                                        <CompaniesList :simple="true" />
                                    </div>
                                </template>
                            </template>
                        </div>

                        <div class="actions" v-if="companiesMode !== 'new'">
                            <CompaniesActions v-if="currentCompanyIndex === null" />

                            <button v-else class="btn-1 btn-s-icon primary-action" @click="close()">
                                <div class="icon-arrow-pos arrow-left"></div>
                                <span>{{$t('common.back_1')}}</span>
                            </button>
                        </div>
                    </div>
                </div>

                <div v-if="hasMediumScreen || !showPanel" class="col-12 col-md-6 col-lg-7 col-xl-8 right-column">
                    <div class="right-section" ref="rightSectionCompany" :class="{ 'toggle-filter-button-active': !hasMediumScreen && !loading && currentCompanyIndex === null && !showPanel }">
                        <CompaniesTitle v-if="!hasMediumScreen && !loading" :showMode="companiesMode !== 'prospects' || !!audienceId" />

                        <div class="header" v-if="total > 0 && !(loading && pagination.page < 2) && companiesMode !== 'new'">
                            <CompaniesHeader
                                :total="total"
                                :pagination="currentCompanyIndex !== null ? pagination : undefined"
                                :i18nString="companiesMode === 'prospects' ? 'views.prospects' : 'views.companies'"
                                v-if="!hasNoCredits"
                            />
                        </div>

                        <div class="main">
                            <LoadingSection v-if="loading" />
                            <SuspectsCompleted v-else-if="companiesMode === 'new' && hasCompletedSuspects" :hasAudience="!!audienceId" />
                            <NoCredits v-else-if="hasNoCredits" />
                            <ErrorSection v-else-if="error" :error="error" />
                            <CompaniesList v-else-if="currentCompanyIndex === null" />
                            <CompanyDetails v-else-if="id === prospect.id" :key="currentCompanyKey" />
                        </div>
                        <div class="companies-footer" v-if="currentCompanyIndex === null && !(loading && pagination.page < 2) && companiesMode !== 'new'">
                            <PaginationPanel
                                :pagination="pagination"
                                :total="total"
                                @update-pagination="handlePaginationUpdate"
                                cssClass="pagination-type-2"
                                :showSpacer="false"
                                :hideArrows="true"
                                :hidePostsPerPage="!hasLargeScreen"
                            />
                        </div>
                        <div class="company-footer" v-else-if="currentCompanyIndex !== null && !hasNoCredits">
                            <CompanyActions />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="!hasMediumScreen && !loading && currentCompanyIndex === null && !showPanel" class="toggle-filter-button">
            <button class="btn-1 btn-s-icon" @click="togglePanel()">
                <div class="icon-filter-pos"></div>
                <span>{{$t(`views.companies.toggle.${showPanel ? 'close' : 'open'}`)}}</span>
            </button>
        </div>
    </div>
</template>

<script>
    import { mapActions, mapState } from 'pinia';
    import { useCompaniesStore } from '@/stores/companies';
    import { useProspectStore } from '@/stores/prospect';
    import { useAudienceStore } from '@/stores/audience';
    import { useScreenWidthStore } from '@/stores/utils/screenWidth';
    import CompanyDetails from '@/components/account/companies/CompanyDetails';
    import CompanyDetailsNav from '@/components/account/companies/CompanyDetailsNav';
    import ProspectsOptions from '@/components/account/companies/prospects/ProspectsOptions';
    import CompaniesOptions from '@/components/account/companies/CompaniesOptions';
    import CompaniesQuickSearch from '@/components/account/companies/CompaniesQuickSearch';
    import CompaniesList from '@/components/account/companies/CompaniesList';
    import CompaniesHeader from '@/components/account/companies/CompaniesHeader';
    import CompaniesTitle from '@/components/account/companies/CompaniesTitle';
    import PaginationPanel from '@/components/panels/pagination/PaginationPanel';
    import CompaniesActions from '@/components/account/companies/CompaniesActions';
    import CompanyActions from '@/components/account/companies/CompanyActions';
    import LoadingSection from '@/components/sections/LoadingSection';
    import ErrorSection from '@/components/sections/ErrorSection';
    import SuspectsCompleted from '@/components/account/companies/misc/SuspectsCompleted';
    import NoCredits from '@/components/account/companies/misc/NoCredits';

    export default {
        name: 'CompaniesManager',
        components: {
            ProspectsOptions,
            CompaniesOptions,
            CompaniesList,
            CompaniesHeader,
            CompaniesTitle,
            CompanyDetails,
            CompanyDetailsNav,
            PaginationPanel,
            CompaniesActions,
            CompanyActions,
            LoadingSection,
            ErrorSection,
            CompaniesQuickSearch,
            SuspectsCompleted,
            NoCredits
        },
        props: {
            id: {
                type: String,
                required: false
            },
            mode: {
                type: String,
                required: false
            },
            audienceId: {
                type: String,
                required: false
            },
            customProspectOptions: {
                type: Object,
                required: false,
                default: () => null
            }
        },
        computed: {
            ...mapState(useScreenWidthStore, ['hasMediumScreen', 'hasLargeScreen']),
            ...mapState(useProspectStore, ['prospect']),
            ...mapState(useProspectStore, { prospectLoading: 'loading', prospectError: 'error' }),
            ...mapState(useCompaniesStore, [ 'nextSuspectParams', 'noCredits', 'showPanel', 'quickSearch', 'companies', 'pagination', 'total', 'hasDefaultOptions', 'hasDefaultProspectsOptions', 'currentCompanyIndex', 'hasCompletedSuspects']),
            ...mapState(useCompaniesStore, { companiesLoading: 'loading', companiesError: 'error', companiesMode: 'mode', companiesAudienceId: 'audienceId' }),
            ...mapState(useAudienceStore, ['audience']),
            currentCompanyKey() {
                return this.id || 'none';
            },
            loading() {
                return Boolean(this.companiesLoading) || Boolean(this.prospectLoading);
            },
            error() {
                return this.companiesError || this.prospectError || false;
            },
            hasDetails() {
                return this.prospect?.details && Object.keys(this.prospect.details).length > 2;
            },
            hasNoCredits() {
                return this.noCredits && (this.companiesMode === 'all' && this.currentCompanyIndex !== null && !this.hasDetails) || (this.companiesMode === 'new' && this.currentCompanyIndex === null);
            }
        },
        methods: {
            ...mapActions(useCompaniesStore, ['togglePanel', 'routeToMode', 'updatePagination', 'loadCompanies', 'loadCompany', 'resetCompany', 'closeCompany', 'getCompanies', 'updateQuickSearch', 'openCompany']),
            ...mapActions(useAudienceStore, ['getAudience', 'resetAudience']),
            handlePaginationUpdate(newPagination) {
                this.updatePagination(newPagination);
            },
            scrollRightSectionToTop() {
                if (this.$refs.rightSectionCompany) {
                    this.$refs.rightSectionCompany.scrollTop = 0;
                }
            },
            abortRequests() {
                const companiesStore = useCompaniesStore();
                if (companiesStore.abortController) {
                    companiesStore.abortController.abort();
                }
            },
            close() {
                if(this.companiesMode === 'new') {
                    this.routeToMode('all');
                } else {
                    this.closeCompany();
                }
            },
            applyCustomProspectOptions() {
                if (this.customProspectOptions && this.mode === 'prospects') {
                    useCompaniesStore().updateOptions(this.customProspectOptions, 'prospects');
                }

                if (this.customProspectOptions && this.mode === 'prospects') {
                    const companiesStore = useCompaniesStore();
                    
                    if (this.customProspectOptions.reset) {
                        companiesStore.resetOptions('prospects');
                        companiesStore.updateOptions(this.customProspectOptions.options, 'prospects');
                    } else {
                        companiesStore.updateOptions(this.customProspectOptions.options, 'prospects');
                    }
                }
            }
        },
        watch: {
            prospect: {
                handler(newVal) {
                    if (newVal && this.currentCompanyIndex !== null && this.companies?.length > 0 && this.companies[this.currentCompanyIndex] && this.companies[this.currentCompanyIndex].id === newVal.id) {
                        this.companies[this.currentCompanyIndex] = newVal;
                    } else if (this.id && !newVal && this.currentCompanyIndex === null && (!this.companies || this.companies.length < 1)) {
                        // Legacy case: if the companiesStore resets through unrelated factors (like subscription changes), we need to close the company
                        this.closeCompany();
                    }
                },
                deep: true
            },
            audience: {
                handler(newVal) {
                    if(!newVal && !!this.audienceId) {
                        this.routeToMode(this.companiesMode, true);
                    } else if (newVal && (!this.audienceId || newVal.audience_id !== this.audienceId) ) {
                        this.routeToMode(this.companiesMode);
                    } else if (newVal && this.audienceId && newVal.audience_id === this.audienceId) {
                        useCompaniesStore().updateAudience(newVal);
                        this.getCompanies(true);
                    }
                },
                deep: true
            },
            hasMediumScreen: {
                handler(newVal) {
                    if (newVal === true || newVal === false) {
                        this.togglePanel(false);
                    }

                    if(newVal === false && this.quickSearch) {
                        this.updateQuickSearch();
                    }
                }
            },
            id: {
                async handler(newVal, oldVal) {
                    if (newVal) {
                        if(oldVal && newVal === oldVal) return;
                        const companyLoaded = await this.loadCompany(newVal);
                        if(!companyLoaded) {
                            this.closeCompany();
                        }
                    } else {
                        this.closeCompany();
                    }
                }
            },
            customProspectOptions: {
                handler(newOptions) {
                    if (newOptions && this.mode === 'prospects') {
                        this.applyCustomProspectOptions();
                    }
                },
                immediate: true
            },
            mode: {
                handler(newVal) {
                    if(newVal && newVal !== this.companiesMode) {
                        this.abortRequests();
                        useCompaniesStore().updateMode(newVal);
                        this.getCompanies(true);
                    }
                },
            },
            audienceId: {
                async handler(newVal) {
                    if (newVal) {
                        const audience = await this.getAudience(newVal);
                        if(audience && newVal !== this.companiesAudienceId) {
                            useCompaniesStore().updateAudience(audience);
                            this.getCompanies(true);
                        } else if (!audience) {
                            this.routeToMode(this.companiesMode, true);
                        }
                    } else {
                        this.routeToMode(this.companiesMode, true);
                    }
                }
            },
            currentCompanyKey() {
                this.scrollRightSectionToTop();
            },
            hasCompletedSuspects: {
                handler(newVal) {
                    if(newVal && this.currentCompanyIndex !== null) {
                        this.closeCompany();
                    }
                },
            },
        },
        async mounted() {
            let modeChanged = false;
            let audienceChanged = false;
            let optionsApplied = false;

            if(this.audienceId) {
                const audience = await this.getAudience(this.audienceId);
                if(audience && this.audienceId !== this.companiesAudienceId) {
                    useCompaniesStore().updateAudience(audience);
                    audienceChanged = true;
                } else if (!audience) {
                    this.routeToMode(this.companiesMode, true);
                    return;
                }
            } else {
                if(this.companiesAudienceId) {
                    useCompaniesStore().updateAudience();
                    audienceChanged = true;
                }
                this.resetAudience();
            }

            if(this.mode) {
                if(this.mode !== this.companiesMode) {
                    useCompaniesStore().updateMode(this.mode);
                    modeChanged = true;
                }

                if(this.mode === 'prospects' && this.customProspectOptions) {
                    this.applyCustomProspectOptions();
                    optionsApplied = true;
                }
            }
            if (this.id) {
                const companyLoaded = await this.loadCompany(this.id);
                if(!companyLoaded) {
                    this.closeCompany();
                }
            } else if (this.companiesMode === 'new' && this.companies?.length > 0 && this.prospect?.id && this.nextSuspectParams ) {
                this.openCompany({id: this.prospect.id});
            } else {
                this.resetCompany();

                if(modeChanged) {
                    this.abortRequests();
                }
                this.getCompanies(this.companies?.length < 2 || modeChanged || audienceChanged || optionsApplied || this.companiesMode === 'new');
            }
        },
        unmounted() {
            this.abortRequests();
        }
    }
</script>